import type { RunStateKey } from "./runStates";
import type {
  CoverageSection,
  GeneralSection,
  RumiRun,
  RunCost,
  RunStateCard,
  SectionMeta,
} from "./types";
import type { CoverageTypeEnum, RumiNullableRumiRun } from "@platform/api";

/** The RFP's own details, which the portal holds and the Run does not. */
export interface RfpFacts {
  /** The numeric RFP id the portal routes on. */
  readonly rfpId: string;
  readonly situsState: string | null;
  readonly eligibleEmployees: number | null;
  /** The carriers writing the RFP's products today, as the broker named them. */
  readonly incumbentCarriers: readonly string[];
  readonly carriersToMarket: readonly string[];
}

type LatestRun = NonNullable<RumiNullableRumiRun>;
type RunResults = NonNullable<LatestRun["results"]>;
type RumiCoverageResult = RunResults["coverages"][number];
type RumiCoverageRow = RumiCoverageResult["rows"][number];
type RumiFindings = RunResults["clientDetails"];
type RunState = Exclude<RunStateKey, "not-reported">;

const AWAITING_INPUT: RunState = "awaiting-input";
const PARTIALLY_EXTRACTED: RunState = "partially-pre-populated";

const COVERAGE_TYPES: Record<CoverageTypeEnum, true> = {
  dental: true,
  vision: true,
  std: true,
  ltd: true,
  basicLifeAdnd: true,
  voluntaryLifeAdnd: true,
  accident: true,
  criticalIllness: true,
  hospitalIndemnity: true,
  cancer: true,
  fmla: true,
  eap: true,
  individualDisabilityInsurance: true,
  wholeLife: true,
};

const isCoverageType = (value: string): value is CoverageTypeEnum =>
  Object.hasOwn(COVERAGE_TYPES, value);

/** btx's coverage status words, in the Launchpad's vocabulary. */
const ROW_STATES: Record<RumiCoverageRow["status"], RunState> = {
  complete: "complete",
  incomplete: PARTIALLY_EXTRACTED,
  "awaiting-input": AWAITING_INPUT,
};

/** Least finished first: a coverage takes its least finished row's state. */
const STATES_BY_URGENCY: readonly RunState[] = [
  AWAITING_INPUT,
  PARTIALLY_EXTRACTED,
  "complete",
];

const NO_FINDINGS = "Rumi recorded nothing about this.";
const MS_PER_SECOND = 1000;

const counted = (count: number, singular: string, plural: string): string =>
  `${count} ${count === 1 ? singular : plural}`;

/** A count the BFF could not read, because its service did not answer. */
type Count = number | null;

/** One breakdown clause: the count, or that it is unavailable. */
const clause = (
  count: Count,
  singular: string,
  plural: string,
  unavailable: string,
): string =>
  count === null
    ? `${unavailable} unavailable`
    : counted(count, singular, plural);

/** The known counts' total. A count that is unavailable is left out. */
const itemsHeadline = (counts: readonly Count[]): string =>
  counted(
    counts.reduce<number>((total, count) => total + (count ?? 0), 0),
    "item",
    "items",
  );

const followUpsMeta = (findings: RumiFindings): SectionMeta[] =>
  findings.followUps.length > 0
    ? [
        {
          label: counted(findings.followUps.length, "follow-up", "follow-ups"),
          tone: AWAITING_INPUT,
        },
      ]
    : [];

const statementsOf = (findings: RumiFindings): string[] =>
  [...findings.summary, ...findings.notes, ...findings.followUps].map(
    (statement) => statement.text,
  );

/**
 * A section btx gives no status: follow-ups await an analyst, findings with
 * none are complete, and no findings at all are not reported.
 */
const findingsState = (findings: RumiFindings): RunStateKey => {
  if (findings.followUps.length > 0) {
    return AWAITING_INPUT;
  }

  return statementsOf(findings).length > 0 ? "complete" : "not-reported";
};

const generalSection = (
  id: string,
  title: string,
  findings: RumiFindings,
  metas: readonly SectionMeta[] = [],
): GeneralSection => {
  const statements = statementsOf(findings);

  return {
    id,
    title,
    state: findingsState(findings),
    metas: [...metas, ...followUpsMeta(findings)],
    finding: statements.length > 0 ? statements.join(" ") : NO_FINDINGS,
  };
};

const generalSections = (results: RunResults): GeneralSection[] => {
  const { byDocument, ...documents } = results.documents;
  const documentFindings: RumiFindings = {
    summary: [
      ...documents.summary,
      ...byDocument.flatMap((document) => document.summary),
    ],
    notes: [
      ...documents.notes,
      ...byDocument.flatMap((document) => document.notes),
    ],
    followUps: [
      ...documents.followUps,
      ...byDocument.flatMap((document) => document.followUps),
    ],
  };

  return [
    generalSection("client-details", "Client details", results.clientDetails),
    generalSection(
      "rfp-and-broker-details",
      "RFP and broker details",
      results.rfpDetails,
    ),
    generalSection(
      "documents",
      "Documents",
      documentFindings,
      byDocument.length > 0
        ? [{ label: counted(byDocument.length, "document", "documents") }]
        : [],
    ),
  ];
};

const coverageState = (coverage: RumiCoverageResult): RunStateKey => {
  const rowStates = new Set(coverage.rows.map((row) => ROW_STATES[row.status]));

  return (
    STATES_BY_URGENCY.find((state) => rowStates.has(state)) ?? "not-reported"
  );
};

/**
 * Rows of one product type share its plan count, which spans both policy
 * periods, so each product type counts once.
 */
const planCountOf = (coverage: RumiCoverageResult): number =>
  [
    ...new Map(
      coverage.rows.map((row) => [row.productType, row.planCount]),
    ).values(),
  ].reduce((total, count) => total + count, 0);

const coverageSection = (coverage: RumiCoverageResult): CoverageSection => {
  if (!isCoverageType(coverage.coverageType)) {
    throw new Error(`Unknown coverage type: ${coverage.coverageType}`);
  }

  const planCount = planCountOf(coverage);

  return {
    // btx holds one coverage per type on an RFP.
    id: coverage.coverageType,
    coverageType: coverage.coverageType,
    title: coverage.name,
    state: coverageState(coverage),
    planCount,
    metas: [
      { label: counted(planCount, "plan", "plans") },
      ...followUpsMeta(coverage),
    ],
    findings: statementsOf(coverage),
  };
};

type RunCounts = RunResults["counts"];

const completeCard = ({
  coveragesWritten,
  coveragesRequested,
  plansWritten,
  plansRequested,
  documentsRead,
  evidenceCited,
}: RunCounts["complete"]): RunStateCard => ({
  state: "complete",
  headline:
    coveragesWritten === null || coveragesRequested === null
      ? "Unavailable"
      : `${coveragesWritten} of ${coveragesRequested}`,
  caption: "Coverages written",
  breakdown: [
    plansWritten === null || plansRequested === null
      ? "Plans written unavailable"
      : `${counted(plansWritten, "plan", "plans")} written of ${plansRequested} requested`,
    clause(documentsRead, "document read", "documents read", "Documents read"),
    clause(
      evidenceCited,
      "evidence entry cited",
      "evidence entries cited",
      "Evidence cited",
    ),
  ],
});

const partiallyExtractedCard = ({
  sourceConflicts,
  staleOrIncompleteSources,
  toConfirm,
}: RunCounts["partiallyExtracted"]): RunStateCard => ({
  state: PARTIALLY_EXTRACTED,
  headline: itemsHeadline([
    sourceConflicts,
    staleOrIncompleteSources,
    toConfirm,
  ]),
  caption: "Filled, needs confirmation",
  breakdown: [
    counted(sourceConflicts, "source conflict", "source conflicts"),
    counted(
      staleOrIncompleteSources,
      "stale or incomplete source",
      "stale or incomplete sources",
    ),
    counted(toConfirm, "to confirm", "to confirm"),
  ],
});

const awaitingInputCard = ({
  requiredFields,
  missingContributions,
  coveragesNotWritten,
}: RunCounts["awaitingInput"]): RunStateCard => ({
  state: AWAITING_INPUT,
  headline: itemsHeadline([
    requiredFields,
    missingContributions,
    coveragesNotWritten,
  ]),
  caption: "Needed before submission",
  breakdown: [
    clause(
      requiredFields,
      "required field",
      "required fields",
      "Required fields",
    ),
    clause(
      missingContributions,
      "employer contribution",
      "employer contributions",
      "Employer contributions",
    ),
    clause(
      coveragesNotWritten,
      "coverage not written",
      "coverages not written",
      "Coverages not written",
    ),
  ],
});

const stateCards = (counts: RunCounts): RunStateCard[] => [
  completeCard(counts.complete),
  partiallyExtractedCard(counts.partiallyExtracted),
  awaitingInputCard(counts.awaitingInput),
];

const runCost = (cost: LatestRun["runCost"]): RunCost | null =>
  cost && {
    turns: cost.modelTurns,
    inputTokens: cost.inputTokens,
    outputTokens: cost.outputTokens,
    model: cost.model,
  };

const byTitle = (a: CoverageSection, b: CoverageSection): number =>
  a.title.localeCompare(b.title);

/**
 * The Launchpad's view of an ended Run. `run` must be the BFF's ended Run,
 * which always carries its results.
 */
export const toLaunchpadRun = (run: LatestRun, rfp: RfpFacts): RumiRun => {
  const { results, endedAt } = run;
  if (!results || !endedAt) {
    throw new Error("toLaunchpadRun needs an ended Run with its results");
  }

  const coverageSections = results.coverages.map(coverageSection).sort(byTitle);
  const narrative = statementsOf(results.rfp).join(" ");

  return {
    rfpId: rfp.rfpId,
    meta: {
      durationSeconds:
        (Date.parse(endedAt) - Date.parse(run.startedAt)) / MS_PER_SECOND,
      cost: runCost(run.runCost),
    },
    narrative: narrative || null,
    atAGlance: {
      situsState: rfp.situsState,
      eligibleEmployees: rfp.eligibleEmployees,
      coverages: coverageSections.map((section) => section.title),
      incumbentCarriers: rfp.incumbentCarriers,
      newCarriers: rfp.carriersToMarket.filter(
        (carrier) => !rfp.incumbentCarriers.includes(carrier),
      ),
    },
    stateCards: stateCards(results.counts),
    generalSections: generalSections(results),
    coverageSections,
  };
};
